/**
 * Weighted key-trend scoring for RCA measurement trends (100 points max per mode).
 *
 * Behaviors sum to 100 points. A trend is a **key trend** when `total` is strictly
 * greater than or equal to `keyTrendScoreThreshold`; remaining sensors use total
 * strictly less than that threshold. Used by Key Sensor Signals and Remaining Sensors.
 *
 * **Use Alarm Limits on** (40 + 24 + 20 + 16 = 100):
 * - Threshold breach (40): share of samples strictly above Hi or below Lo; scales
 *   linearly with breach rate; 20% breach rate awards full 40 points.
 * - Drift (24): |shift/range| as percent (same windows as Pre-Shutdown Drift Summary);
 *   linear anchors — 0 below 15%, 1 at 15%, 12 at 22.5%, 24 at 30%+.
 * - Step change (20): baseline μ/σ from first 50% of samples; compare last-25%
 *   mean and latest value (two |z| scores). 0 below 1.5σ each; each half scales
 *   linearly to full at 3σ (max 10 + 10 pts).
 * - Threshold approach (16): last-3-point slope projection at a local min/max
 *   crosses Lo or Hi (see `isTrendNearLowAlarmBand` / `isTrendNearHighAlarmBand`).
 *
 * **Use Alarm Limits off** (50 + 24 + 26 = 100):
 * - Z-score (50): scales |ZScore| from 0 at z=0 to 50 at z ≥
 *   z-score threshold.
 * - Drift (24): same as above.
 * - Step change (26): same step-change logic as alarm mode (13 + 13 pts at cap).
 *
 * @module rca-key-trend-score
 */

import { RCA_MEASUREMENTS_CONFIG_DEFAULTS } from "@/components/root-cause-analysis/rca-measurements-config-storage.js";
import {
  getSortedTrendPointsFromRow,
  hasPlottableMeasurements,
  isTrendNearHighAlarmBand,
  isTrendNearLowAlarmBand,
} from "@/components/root-cause-analysis/rca-measurement-trend-shared.jsx";
import { computeTrendDrift } from "@/components/root-cause-analysis/pre-shutdown-drift-summary.jsx";
import { formatDetailOneDecimal } from "./root-cause-analysis-helpers";

/**
 * @param {{ keyTrendScoreThreshold?: number }} options
 */
function resolveKeyTrendScoreThreshold(options) {
  const threshold = Number(options.keyTrendScoreThreshold);
  return Number.isFinite(threshold) && threshold >= 0
    ? threshold
    : RCA_MEASUREMENTS_CONFIG_DEFAULTS.keyTrendScoreThreshold;
}

/** Default drift window settings (aligned with drift summary). */ const DEFAULT_FIRST_WINDOW_PCT =
  100 / 3;
const DEFAULT_LAST_WINDOW_HOURS = 2;
const DEFAULT_DRIFT_SHIFT_THRESHOLD_PCT = 15;

/** Maximum points per trend (both scoring modes). */
export const KEY_TREND_SCORE_MAX = 100;

const DRIFT_SCORE_MAX = 24;
const DRIFT_SCORE_MIN_PCT = 15;
const DRIFT_SCORE_MID_PCT = 22.5;
const DRIFT_SCORE_MAX_PCT = 30;

/** Step change: |z| must exceed this to earn any points (per component). */
const STEP_CHANGE_Z_FLAG = 1.5;
/** Step change: |z| at or above this awards full share for that component. */
const STEP_CHANGE_Z_CAP = 3;

/**
 * Per-behavior point contributions and combined total for one measurement row.
 *
 * @typedef {Object} KeyTrendScoreBreakdown
 * @property {number} total — Sum of active behaviors (max 100).
 * @property {number} thresholdBreach — Alarm mode only; 0–40.
 * @property {number} drift — 0–24.
 * @property {number} stepChange — 0–20 (alarm) or 0–26 (no alarm).
 * @property {number} thresholdApproach — Alarm mode only; 0 or 16.
 * @property {number} zScore — No-alarm mode only; 0–50.
 */

/**
 * @typedef {{ id: string, label: string, maxPoints: number, earned: number, detail?: string }} KeyTrendScoreBehaviorDisplay
 */

/**
 * @param {number} value
 * @param {number} minIn
 * @param {number} maxIn
 * @param {number} minOut
 * @param {number} maxOut
 */
function interpolateScore(value, minIn, maxIn, minOut, maxOut) {
  if (
    !Number.isFinite(value)
    || !Number.isFinite(minIn)
    || !Number.isFinite(maxIn)
  ) {
    return minOut;
  }
  if (value <= minIn) return minOut;
  if (value >= maxIn) return maxOut;
  const t = (value - minIn) / (maxIn - minIn);
  return minOut + t * (maxOut - minOut);
}

/**
 * Take the ceiling of each behavior score; total is the sum of ceiling treated components.
 * @param {KeyTrendScoreBreakdown} breakdown
 * @returns {KeyTrendScoreBreakdown}
 */
function ceilKeyTrendScoreBreakdown(breakdown) {
  const thresholdBreach = Math.ceil(breakdown.thresholdBreach);
  const drift = Math.ceil(breakdown.drift);
  const stepChange = Math.ceil(breakdown.stepChange);
  const thresholdApproach = Math.ceil(breakdown.thresholdApproach);
  const zScore = Math.ceil(breakdown.zScore);
  return {
    thresholdBreach,
    drift,
    stepChange,
    thresholdApproach,
    zScore,
    total: thresholdBreach + drift + stepChange + thresholdApproach + zScore,
  };
}

/**
 * Score drift behavior (max 24 points).
 *
 * Uses absolute drift percent: |mean(last window) − mean(first window)| / (max − min)
 * × 100, matching Pre-Shutdown Drift Summary. Piecewise linear between anchors:
 * 0 below 15%, 1 at 15%, 12 at 22.5%, 24 at 30% and above.
 *
 * @param {number} absDriftPct — |shift/range| as percent (0–100 scale).
 * @returns {number} Points in [0, 24].
 */
function scoreDriftBehavior(absDriftPct) {
  if (!Number.isFinite(absDriftPct) || absDriftPct <= DRIFT_SCORE_MIN_PCT) {
    return 0;
  }
  if (absDriftPct >= DRIFT_SCORE_MAX_PCT) {
    return DRIFT_SCORE_MAX;
  }
  if (absDriftPct <= DRIFT_SCORE_MID_PCT) {
    return interpolateScore(
      absDriftPct,
      DRIFT_SCORE_MIN_PCT,
      DRIFT_SCORE_MID_PCT,
      1,
      12
    );
  }
  return interpolateScore(
    absDriftPct,
    DRIFT_SCORE_MID_PCT,
    DRIFT_SCORE_MAX_PCT,
    12,
    DRIFT_SCORE_MAX
  );
}

/**
 * Score threshold breach behavior (max 40 points; alarm limits mode only).
 *
 * Counts trend samples strictly above Hi or strictly below Lo, divided by total
 * sample count. Breach rate maps linearly to points: 0% → 0 pts, 20%+ → 40 pts
 * (capped). Returns 0 when neither Hi nor Lo is defined.
 *
 * @param {MeasurementWithStatsRow|Record<string, unknown>} row
 * @returns {number} Points in [0, 40].
 */
function scoreThresholdBreachBehavior(row) {
  const hi = Number(row.Hi);
  const lo = Number(row.Lo);
  const hasHi = Number.isFinite(hi);
  const hasLo = Number.isFinite(lo);
  if (!hasHi && !hasLo) return 0;

  const points = getSortedTrendPointsFromRow(row);
  if (points.length === 0) return 0;

  let breaches = 0;
  for (const p of points) {
    if (hasHi && p.y > hi) breaches += 1;
    else if (hasLo && p.y < lo) breaches += 1;
  }

  const breachRate = breaches / points.length;
  return Math.min(40, (breachRate / 0.2) * 40);
}

/**
 * @param {number[]} values
 */
function meanOf(values) {
  if (values.length === 0) return NaN;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

/**
 * @param {number[]} values
 */
function stdDevOf(values) {
  if (values.length < 2) return NaN;
  const mean = meanOf(values);
  const variance =
    values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / values.length;
  const sd = Math.sqrt(variance);
  return Number.isFinite(sd) && sd > 1e-12 ? sd : NaN;
}

/**
 * |z| of a value vs baseline μ and σ.
 * @param {number} value
 * @param {number} baselineMean
 * @param {number} baselineStd
 */
function absZVsBaseline(value, baselineMean, baselineStd) {
  if (
    !Number.isFinite(value)
    || !Number.isFinite(baselineMean)
    || !Number.isFinite(baselineStd)
    || baselineStd <= 1e-12
  ) {
    return NaN;
  }
  return Math.abs((value - baselineMean) / baselineStd);
}

/**
 * Points for one step-change |z| component: 0 at ≤1.5σ, linear to `maxShare` at 3σ+.
 * @param {number} absZ
 * @param {number} maxShare
 */
function stepChangePointsFromAbsZ(absZ, maxShare) {
  if (!Number.isFinite(absZ) || absZ <= STEP_CHANGE_Z_FLAG) return 0;
  if (absZ >= STEP_CHANGE_Z_CAP) return maxShare;
  return interpolateScore(
    absZ,
    STEP_CHANGE_Z_FLAG,
    STEP_CHANGE_Z_CAP,
    0,
    maxShare
  );
}

/**
 * Two |z| scores for step change: mean of last 25% and latest sample vs first-50% baseline.
 *
 * @param {MeasurementWithStatsRow|Record<string, unknown>} row
 * @returns {{ zMeanLast25: number, zLatest: number }}
 */
function computeStepChangeZScores(row) {
  const points = getSortedTrendPointsFromRow(row);
  if (points.length < 2) {
    return { zMeanLast25: NaN, zLatest: NaN };
  }

  const baselineCount = Math.max(1, Math.floor(points.length * 0.5));
  const recentCount = Math.max(1, Math.ceil(points.length * 0.25));
  const baselineYs = points.slice(0, baselineCount).map((p) => p.y);
  const recentYs = points.slice(-recentCount).map((p) => p.y);

  const baselineMean = meanOf(baselineYs);
  let baselineStd = stdDevOf(baselineYs);
  if (!Number.isFinite(baselineStd)) {
    baselineStd = Number(row.StdDev);
  }

  const meanLast25 = meanOf(recentYs);
  const latestY = points[points.length - 1].y;

  return {
    zMeanLast25: absZVsBaseline(meanLast25, baselineMean, baselineStd),
    zLatest: absZVsBaseline(latestY, baselineMean, baselineStd),
  };
}

/**
 * Score step-change behavior (alarm mode max 20; no-alarm max 26).
 *
 * Baseline μ/σ from the first 50% of trend samples (σ falls back to `row.StdDev`).
 * Two components, each up to half of `maxPoints`: |z| of the mean of the last 25%
 * and |z| of the latest sample. Each scales from 0 at |z| ≤ 1.5σ to full share at 3σ+.
 *
 * @param {MeasurementWithStatsRow|Record<string, unknown>} row
 * @param {number} [maxPoints=20]
 * @returns {number} Points in [0, maxPoints].
 */
function scoreStepChangeBehavior(row, maxPoints = 20) {
  const cap = Number.isFinite(maxPoints) && maxPoints > 0 ? maxPoints : 20;
  const halfCap = cap / 2;
  const { zMeanLast25, zLatest } = computeStepChangeZScores(row);

  return (
    stepChangePointsFromAbsZ(zMeanLast25, halfCap)
    + stepChangePointsFromAbsZ(zLatest, halfCap)
  );
}

/**
 * Score z-score behavior (max 50 points; no-alarm limits mode only).
 *
 * Uses |z| of the most recent value. Linear from 0 points at z = 0 to 50 points
 * when |z| ≥ z-score threshold (default threshold 3 if invalid).
 *
 * @param {number} z — Typically `row.ZScore`.
 * @param {number} zScoreThreshold
 * @returns {number} Points in [0, 50].
 */
function scoreZScoreBehavior(z, zScoreThreshold) {
  const zMin =
    Number.isFinite(zScoreThreshold) && zScoreThreshold > 0
      ? zScoreThreshold
      : 3;
  if (!Number.isFinite(z)) return 0;
  const absZ = Math.abs(z);
  if (absZ >= zMin) return 50;
  return (absZ / zMin) * 50;
}

/**
 * Score threshold-approach behavior (0 or 16 points; alarm limits mode only).
 *
 * 16 points when the last-3-point slope projection at a local minimum is below Lo
 * or at a local maximum exceeds Hi. Does not scan full history (breaches are scored
 * separately via `scoreThresholdBreachBehavior`).
 *
 * @param {MeasurementWithStatsRow|Record<string, unknown>} row
 * @returns {number} 16 or 0.
 */
function scoreThresholdApproachBehavior(row) {
  return isTrendNearLowAlarmBand(row) || isTrendNearHighAlarmBand(row) ? 16 : 0;
}

/**
 * Compute the full key-trend score breakdown for one measurement row.
 *
 * Selects alarm vs no-alarm behavior set from `options.useAlarmLimits`, computes
 * drift via `computeTrendDrift` (optional window overrides), and sums components.
 * See module doc for point budgets and methodology.
 *
 * @param {MeasurementWithStatsRow|Record<string, unknown>} row
 * @param {{
 *   useAlarmLimits: boolean,
 *   zScoreThreshold: number,
 *   firstWindowPct?: number,
 *   lastWindowHours?: number,
 *   driftShiftThresholdPct?: number,
 * }} options
 * @returns {KeyTrendScoreBreakdown} Integer points (each behavior ceiled; total = sum).
 */
function computeKeyTrendScore(row, options) {
  const useAlarmLimits = options.useAlarmLimits !== false;
  const zScoreThreshold = Number(options.zScoreThreshold);
  const driftResult = computeTrendDrift(row, {
    firstWindowPct: options.firstWindowPct ?? DEFAULT_FIRST_WINDOW_PCT,
    lastWindowHours: options.lastWindowHours ?? DEFAULT_LAST_WINDOW_HOURS,
    shiftThresholdPct:
      options.driftShiftThresholdPct ?? DEFAULT_DRIFT_SHIFT_THRESHOLD_PCT,
  });
  const absDriftPct = Number.isFinite(driftResult.drift)
    ? Math.abs(driftResult.drift) * 100
    : NaN;

  const drift = scoreDriftBehavior(absDriftPct);

  if (useAlarmLimits) {
    const thresholdBreach = scoreThresholdBreachBehavior(row);
    const stepChange = scoreStepChangeBehavior(row, 20);
    const thresholdApproach = scoreThresholdApproachBehavior(row);
    return ceilKeyTrendScoreBreakdown({
      thresholdBreach,
      drift,
      stepChange,
      thresholdApproach,
      zScore: 0,
      total: 0,
    });
  }

  const zScore = scoreZScoreBehavior(Number(row.ZScore), zScoreThreshold);
  const stepChangeCapped = scoreStepChangeBehavior(row, 26);
  return ceilKeyTrendScoreBreakdown({
    thresholdBreach: 0,
    drift,
    stepChange: stepChangeCapped,
    thresholdApproach: 0,
    zScore,
    total: 0,
  });
}

/**
 * @param {MeasurementWithStatsRow|Record<string, unknown>} row
 * @returns {{ breaches: number, total: number }}
 */
function countThresholdBreachReadings(row) {
  const hi = Number(row.Hi);
  const lo = Number(row.Lo);
  const hasHi = Number.isFinite(hi);
  const hasLo = Number.isFinite(lo);
  const points = getSortedTrendPointsFromRow(row);
  if (points.length === 0 || (!hasHi && !hasLo)) {
    return { breaches: 0, total: 0 };
  }
  let breaches = 0;
  for (const p of points) {
    if (hasHi && p.y > hi) breaches += 1;
    else if (hasLo && p.y < lo) breaches += 1;
  }
  return { breaches, total: points.length };
}

/**
 * @param {string} behaviorId
 * @param {MeasurementWithStatsRow|Record<string, unknown>} row
 * @param {{ firstWindowPct?: number, lastWindowHours?: number, driftShiftThresholdPct?: number }} [driftOptions]
 * @returns {string|undefined}
 */
function getKeyTrendBehaviorDetailText(behaviorId, row, driftOptions) {
  const driftCfg = {
    firstWindowPct: driftOptions?.firstWindowPct ?? DEFAULT_FIRST_WINDOW_PCT,
    lastWindowHours: driftOptions?.lastWindowHours ?? DEFAULT_LAST_WINDOW_HOURS,
    shiftThresholdPct:
      driftOptions?.driftShiftThresholdPct ?? DEFAULT_DRIFT_SHIFT_THRESHOLD_PCT,
  };

  switch (behaviorId) {
    case "thresholdBreach": {
      const { breaches, total } = countThresholdBreachReadings(row);
      if (total === 0) return undefined;
      return ` · ${breaches} of ${total} readings`;
    }
    case "drift": {
      const { drift, up } = computeTrendDrift(row, driftCfg);
      if (!Number.isFinite(drift)) return undefined;
      const pct = (Math.abs(drift) * 100).toFixed(1);
      const dir = up ? "up" : "down";
      return ` · Mean shifted ${dir} by ${pct}% of the range`;
    }
    case "stepChange": {
      const points = getSortedTrendPointsFromRow(row);
      if (points.length < 2) return undefined;
      const baselineCount = Math.max(1, Math.floor(points.length * 0.5));
      const recentCount = Math.max(1, Math.ceil(points.length * 0.25));
      const baselineMean = meanOf(
        points.slice(0, baselineCount).map((p) => p.y)
      );
      const recentMean = meanOf(points.slice(-recentCount).map((p) => p.y));
      const { zMeanLast25, zLatest } = computeStepChangeZScores(row);
      const z = Math.max(
        Number.isFinite(zMeanLast25) ? zMeanLast25 : 0,
        Number.isFinite(zLatest) ? zLatest : 0
      );
      if (z <= STEP_CHANGE_Z_FLAG) return undefined;
      return ` · ${z.toFixed(1)}σ vs baseline (${formatDetailOneDecimal(baselineMean)} → ${formatDetailOneDecimal(recentMean)})`;
    }
    default:
      return undefined;
  }
}

/**
 * Behaviors for the left-column score pills (alarm vs no-alarm mode).
 *
 * @param {KeyTrendScoreBreakdown} score
 * @param {boolean} useAlarmLimits
 * @returns {KeyTrendScoreBehaviorDisplay[]}
 */
function getKeyTrendScoreBehaviorsForDisplay(score, useAlarmLimits) {
  if (useAlarmLimits !== false) {
    return [
      {
        id: "thresholdBreach",
        label: "Breached Alarm Threshold",
        maxPoints: 40,
        earned: score.thresholdBreach,
      },
      {
        id: "drift",
        label: "Pre-Shutdown Drift",
        maxPoints: DRIFT_SCORE_MAX,
        earned: score.drift,
      },
      {
        id: "stepChange",
        label: "Step Change",
        maxPoints: 20,
        earned: score.stepChange,
      },
      {
        id: "thresholdApproach",
        label: "Approaching Alarm Threshold",
        maxPoints: 16,
        earned: score.thresholdApproach,
      },
    ];
  }
  return [
    {
      id: "zScore",
      label: "Elevated Z-Score",
      maxPoints: 50,
      earned: score.zScore,
    },
    {
      id: "drift",
      label: "Pre-Shutdown Drift",
      maxPoints: DRIFT_SCORE_MAX,
      earned: score.drift,
    },
    {
      id: "stepChange",
      label: "Step Change",
      maxPoints: 26,
      earned: score.stepChange,
    },
  ];
}

/**
 * @param {KeyTrendScoreBreakdown} score
 * @param {MeasurementWithStatsRow|Record<string, unknown>} row
 * @param {boolean} useAlarmLimits
 * @param {{ firstWindowPct?: number, lastWindowHours?: number, driftShiftThresholdPct?: number }} [driftOptions]
 * @returns {KeyTrendScoreBehaviorDisplay[]}
 */
export function getKeyTrendScoreBehaviorsForDisplayWithDetails(
  score,
  row,
  useAlarmLimits,
  driftOptions
) {
  return getKeyTrendScoreBehaviorsForDisplay(score, useAlarmLimits).map(
    (behavior) => ({
      ...behavior,
      detail: getKeyTrendBehaviorDetailText(behavior.id, row, driftOptions),
    })
  );
}

/**
 * Filter plottable rows to key trends (score ≥ threshold). Sorting is applied
 * by `sortKeyedTrendEntries` in `rca-measurement-sort.js`.
 *
 * @param {unknown[]} rows
 * @param {{
 *   useAlarmLimits: boolean,
 *   zScoreThreshold: number,
 *   keyTrendScoreThreshold: number,
 *   firstWindowPct?: number,
 *   lastWindowHours?: number,
 *   driftShiftThresholdPct?: number,
 * }} options
 * @returns {{ row: unknown, score: KeyTrendScoreBreakdown }[]}
 */
export function rankKeyTrends(rows, options) {
  if (!Array.isArray(rows)) return [];
  const threshold = resolveKeyTrendScoreThreshold(options);

  return rows
    .filter((row) => hasPlottableMeasurements(row))
    .map((row) => ({
      row,
      score: computeKeyTrendScore(row, options),
    }))
    .filter(({ score }) => score.total >= threshold);
}

/**
 * Plottable rows with total score strictly below threshold. Sorting is applied
 * by `sortKeyedTrendEntries` in `rca-measurement-sort.js`.
 *
 * @param {unknown[]} rows
 * @param {{
 *   useAlarmLimits: boolean,
 *   zScoreThreshold: number,
 *   keyTrendScoreThreshold: number,
 *   firstWindowPct?: number,
 *   lastWindowHours?: number,
 *   driftShiftThresholdPct?: number,
 * }} options
 * @returns {{ row: unknown, score: KeyTrendScoreBreakdown }[]}
 */
export function rankRemainingTrends(rows, options) {
  if (!Array.isArray(rows)) return [];
  const threshold = resolveKeyTrendScoreThreshold(options);
  return rows
    .filter((row) => hasPlottableMeasurements(row))
    .map((row) => ({
      row,
      score: computeKeyTrendScore(row, options),
    }))
    .filter(({ score }) => score.total < threshold);
}
